/**
 * ContactTab — Contact form and support enquiry submission
 *
 * Submit general enquiries, account questions, and support requests.
 * Submits support enquiries to the `site_issues` table.
 */

import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    MenuItem,
    Alert,
    CircularProgress,
    useTheme,
    Divider,
    Link,
} from '@mui/material';
import {
    Send as SendIcon,
    Email as EmailIcon,
    CheckCircle as SuccessIcon,
} from '@mui/icons-material';
import { supabase } from '../../../lib/supabase';
import { useSupportHub } from '../SupportHubContext';
import { track } from '../../../lib/analytics';

const CATEGORIES = [
    { value: 'general', label: 'General Enquiry' },
    { value: 'account', label: 'Account & Billing' },
    { value: 'technical', label: 'Technical Issue' },
    { value: 'compliance', label: 'Compliance Question' },
    { value: 'feature', label: 'Feature Request' },
    { value: 'other', label: 'Other' },
];

const ContactTab: React.FC = () => {
    const theme = useTheme();
    const { contactPrefill, clearContactPrefill } = useSupportHub();
    const [category, setCategory] = useState('general');
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (contactPrefill) {
            if (contactPrefill.subject) {
                setSubject(contactPrefill.subject);
            }
            if (contactPrefill.message) {
                setMessage(contactPrefill.message);
            }
            clearContactPrefill?.();
        }
    }, [contactPrefill, clearContactPrefill]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!subject.trim() || !message.trim()) return;

        setIsSubmitting(true);
        setError(null);

        try {
            const categoryLabel = CATEGORIES.find(c => c.value === category)?.label || category;
            const urlPath = typeof window !== 'undefined' ? window.location.pathname : '/';
            const fullUrl = typeof window !== 'undefined' ? window.location.href : '';

            const { error: functionError } = await supabase.functions.invoke('send-support-email', {
                body: { category: categoryLabel, subject, message, url_path: urlPath, full_url: fullUrl },
            });

            if (functionError) throw functionError;

            track('support_contact_submitted');

            setSubmitted(true);
            setSubject('');
            setMessage('');
        } catch (err) {
            console.error('Failed to submit support request:', err);
            setError('Failed to submit your request. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <Box
                sx={{
                    textAlign: 'center',
                    py: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1.5,
                }}
            >
                <Box
                    sx={{
                        width: 56,
                        height: 56,
                        borderRadius: '50%',
                        background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <SuccessIcon sx={{ color: '#fff', fontSize: '1.8rem' }} />
                </Box>
                <Typography variant="body1" fontWeight={600}>
                    Message Sent! ✉️
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    We'll get back to you as soon as possible.
                </Typography>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setSubmitted(false)}
                    sx={{ mt: 1 }}
                >
                    Send Another
                </Button>
            </Box>
        );
    }

    return (
        <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
        >
            {/* ── Header ── */}
            <Box>
                <Typography variant="body2" fontWeight={600} mb={0.25}>
                    Get in Touch
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    Send us a message and we'll respond within 24 hours
                </Typography>
            </Box>

            {error && (
                <Alert severity="error" sx={{ py: 0.5, fontSize: '0.78rem' }}>
                    {error}
                </Alert>
            )}

            {/* ── Category ── */}
            <TextField
                select
                size="small"
                label="Category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                fullWidth
                SelectProps={{
                    MenuProps: {
                        sx: { zIndex: 1600 }
                    }
                }}
            >
                {CATEGORIES.map((cat) => (
                    <MenuItem key={cat.value} value={cat.value}>
                        {cat.label}
                    </MenuItem>
                ))}
            </TextField>

            {/* ── Subject ── */}
            <TextField
                size="small"
                label="Subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                fullWidth
                required
                placeholder="Brief summary of your question"
            />

            {/* ── Message ── */}
            <TextField
                size="small"
                label="Message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                fullWidth
                required
                multiline
                rows={4}
                placeholder="Describe your question or issue in detail..."
            />

            {/* ── Submit ── */}
            <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={!subject.trim() || !message.trim() || isSubmitting}
                startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                sx={{
                    py: 1.25,
                    borderRadius: '10px',
                    fontWeight: 600,
                }}
            >
                {isSubmitting ? 'Sending...' : 'Send Message'}
            </Button>

            {/* ── Contact info ── */}
            <Divider sx={{ my: 0.5 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <EmailIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                    Or email us at{' '}
                    <Link href="mailto:support@tradeconnector.com.au" underline="hover">
                        support@tradeconnector.com.au
                    </Link>
                </Typography>
            </Box>
        </Box>
    );
};

export default ContactTab;
